import { endOfWeek, startOfDay } from 'date-fns';
import type { Inquiry } from '../types/api';

/**
 * Issue #52 — activity grouping («По делам», Bitrix-style).
 * Buckets a deal by its next-step date relative to *today* (local time,
 * weeks start Monday):
 *   overdue  — before today
 *   today    — today
 *   thisWeek — later than today, still inside the current calendar week
 *   nextWeek — inside the following calendar week
 *   noDate   — no next-step date set
 *   later    — further out than next week («Больше двух недель»)
 */
export type NextStepBucket = 'overdue' | 'today' | 'thisWeek' | 'nextWeek' | 'noDate' | 'later';

/** Display order of the activity columns. */
export const NEXT_STEP_BUCKETS: readonly NextStepBucket[] = [
  'overdue',
  'today',
  'thisWeek',
  'nextWeek',
  'noDate',
  'later',
];

export function nextStepBucket(nextStepAt: string | null | undefined, now: Date = new Date()): NextStepBucket {
  if (!nextStepAt) return 'noDate';
  const day = startOfDay(new Date(nextStepAt));
  if (Number.isNaN(day.getTime())) return 'noDate';
  const today = startOfDay(now);
  if (day.getTime() < today.getTime()) return 'overdue';
  if (day.getTime() === today.getTime()) return 'today';
  const thisWeekEnd = endOfWeek(today, { weekStartsOn: 1 });
  if (day.getTime() <= thisWeekEnd.getTime()) return 'thisWeek';
  const nextWeekEnd = endOfWeek(new Date(thisWeekEnd.getTime() + 24 * 60 * 60 * 1000), { weekStartsOn: 1 });
  if (day.getTime() <= nextWeekEnd.getTime()) return 'nextWeek';
  return 'later';
}

/** Group open deals into the activity columns (closed deals are excluded —
 *  they have no next step to plan). */
export function groupByNextStep(inquiries: Inquiry[], now: Date = new Date()): Record<NextStepBucket, Inquiry[]> {
  const groups: Record<NextStepBucket, Inquiry[]> = {
    overdue: [],
    today: [],
    thisWeek: [],
    nextWeek: [],
    noDate: [],
    later: [],
  };
  for (const inq of inquiries) {
    if (inq.outcome !== 'NONE') continue;
    groups[nextStepBucket(inq.nextStepAt, now)].push(inq);
  }
  return groups;
}

/** Count of open deals whose next step is already overdue (badge on the
 *  view switcher). */
export function overdueCount(inquiries: Inquiry[], now: Date = new Date()): number {
  return inquiries.filter(
    (i) => i.outcome === 'NONE' && nextStepBucket(i.nextStepAt, now) === 'overdue',
  ).length;
}
